import React, { useEffect, useContext, useState, useRef } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import ReportExcelPrintGap from "../../../components/Reports/ReportExcelPrintGap";

const InvoiceAgeingReport = () => {
  const { t } = useTranslation();
  
  let {
    loading,
    dataPaginating,
    setDataPaginating,
  } = useContext(SettingsContext);
  const { supplierForSearch, getSupplier } = useContext(UserContext);
  const { branchForSearch } = useContext(RestaurantContext);
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  
  const [asOfDate, setAsOfDate] = useState(new Date());
  const [formData, setFormData] = useState({
    branch: null,
    supplier: null,
  });
  
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");

  let common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  useEffect(() => {
    if (!supplierForSearch) {
      getSupplier();
    }
  }, []);

  const handleOption = (e) => {
    setOption(e.target.value);
  };

  const handleSetSelect = (key, value) => {
    setFormData({
      ...formData,
      [key]: value
    });
  };

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `
        @page {
          margin: 15px 7px;
        }
      `,
  });

  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (asOfDate !== null && formData.branch) {
      getReport();
    } else {
      toast.error(
        `${_t(t("Please select date and branch"))}`,
        common_toast
      );
    }
  };

  const getReport = async () => {
    setDataPaginating(true);
    let date = moment(asOfDate).format("YYYY-MM-DD");
    const url = BASE_URL + "/settings/get-invoice-ageing-report";
    let payload = {
      as_of_date: date,
      branch_id: formData.branch.id,
      supplier_id: formData.supplier?.id || "",
    };

    return axios
      .post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setGeneratedReport(true);
        setReportData(res.data);
        setDataPaginating(false);
        if (option === "excel") {
          setTimeout(() => componentRef.current?.handleDownload?.(), 100);
        } else {
          setTimeout(() => thermalPrint(), 100);
        }
      })
      .catch((error) => {
        setDataPaginating(false);
        toast.error(`${_t(t("Please try again"))}`, common_toast);
      });
  };

  const header = [
    { name: "S.No", render: (item, i) => <>{i + 1}</> },
    { name: "Invoice Number", key: "invoice_number" },
    { name: "Last Payment Date", render: (item) => item.last_payment_date ? <Moment format="LL">{item.last_payment_date}</Moment> : "-" },
    { name: "30 Days", key: "30_days", total: 1 },
    { name: "60 Days", key: "60_days", total: 1 },
    { name: "90 Days", key: "90_days", total: 1 },
    { name: "Above 90 Days", key: "above_90_days", total: 1 },
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Invoice Ageing Report"))}</title>
      </Helmet>
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename="Invoice Ageing Report"
        sheet="sheet 1"
        buttonText="Invoice Ageing Report"
        ref={componentRef}
      />

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Invoice Ageing Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end">
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-end">
                        <div className="col-2 d-md-block">
                          <label className="form-label mb-1 table-text">
                            {_t(t("As of Date"))}
                          </label>
                          <DatePicker
                            selected={asOfDate}
                            onChange={(date) => setAsOfDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control table-text"
                            placeholderText={_t(t("As of date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>

                        <div className="col-3">
                          <label className="form-label mb-1 table-text">
                            {_t(t("Branch"))}
                          </label>
                          <Select
                            options={branchForSearch ? branchForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={formData.branch}
                            classNamePrefix="select"
                            isClearable
                            className="table-text"
                            onChange={(branch) => handleSetSelect("branch", branch)}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select a branch")) + ".."}
                          />
                        </div>

                        <div className="col-3">
                          <label className="form-label mb-1 table-text">
                            {_t(t("Supplier"))}
                          </label>
                          <Select
                            options={supplierForSearch ? supplierForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={formData.supplier}
                            classNamePrefix="select"
                            isClearable
                            className="table-text"
                            onChange={(supplier) => handleSetSelect("supplier", supplier)}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select supplier")) + ".."}
                          />
                        </div>

                        <div className="col-2 t-mb-15 mb-md-0 ml-auto text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={(e) => getExcelReport()}
                          >
                            GENERATE REPORT
                          </button>
                        </div>
                      </div>
                      <ReportExcelPrintGap
                        header={header}
                        generatedReport={generatedReport}
                        heading="Invoice Ageing Report"
                        reportData={reportData}
                        startDate={asOfDate}
                        endDate={asOfDate}
                        componentRef={componentRef}
                        printRef={componentRefThermal}
                        branch={{
                          name: formData.branch?.name ?? "",
                          show: true,
                        }}
                        type={option}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default InvoiceAgeingReport;
